// ============================================================
// instanceEdits - adding, removing and editing the talents and traits
// on a sheet, pure.
//
// Each returns the partial an editor hands to `onChange`: a NEW array,
// never an edit in place (projections are memoised on identity). An
// instance is addressed by its INDEX, because the same catalog entry may
// be taken more than once (see TalentInstance).
// ============================================================
import type { Character, TalentInstance, TraitInstance } from './types';
import { TALENT_CATALOG } from './talentCatalog';
import { TRAIT_CATALOG } from './traitCatalog';
import { bodyTypeFor, isCrowd } from '../bodyShape';

/** The id of an entry no catalog defines: a name and a note, no rules effect. */
export const CUSTOM_ENTRY_ID = 'custom';

/** What an editor may change on an instance already taken. */
export type InstancePatch = Partial<Pick<TalentInstance, 'parameter' | 'name' | 'note'>>;

interface CatalogEntry {
  parameterized: boolean;
  repeatable?: boolean;
}

/** May this entry be added: unknown ids never, a taken one only when repeatable. */
function addable(def: CatalogEntry | undefined, taken: boolean): boolean {
  return !!def && (!taken || !!def.repeatable);
}

/** A fresh instance's parameter: what was given, else empty when the entry takes one. */
function parameterFor(def: CatalogEntry, parameter: string | undefined): { parameter?: string } {
  if (parameter !== undefined) return { parameter };
  return def.parameterized ? { parameter: '' } : {};
}

const without = <T>(list: T[], index: number): T[] => list.filter((_, i) => i !== index);
const patched = <T>(list: T[], index: number, patch: InstancePatch): T[] =>
  list.map((inst, i) => (i === index ? { ...inst, ...patch } : inst));

/** A custom entry's fields; the note is left off when empty. */
function customFields(name: string, note: string): { name: string; note?: string } {
  return note ? { name, note } : { name };
}

/** Whether the catalog talent can be added to this sheet right now. */
export function canAddTalent(character: Character, id: string): boolean {
  return addable(
    TALENT_CATALOG[id],
    character.talents.some((t) => t.talentId === id),
  );
}

/** The talent added (nothing changes when it cannot be). */
export function addTalent(character: Character, id: string, parameter?: string): Partial<Character> {
  if (!canAddTalent(character, id)) return {};
  const inst: TalentInstance = { talentId: id, ...parameterFor(TALENT_CATALOG[id], parameter) };
  return { talents: [...character.talents, inst] };
}

/** A talent no catalog knows, written down by name. */
export function addCustomTalent(character: Character, name: string, note = ''): Partial<Character> {
  return { talents: [...character.talents, { talentId: CUSTOM_ENTRY_ID, ...customFields(name, note) }] };
}

export function removeTalentAt(character: Character, index: number): Partial<Character> {
  return { talents: without(character.talents, index) };
}

export function setTalentAt(character: Character, index: number, patch: InstancePatch): Partial<Character> {
  return { talents: patched(character.talents, index, patch) };
}

/** Whether the catalog trait can be added to this sheet right now. */
export function canAddTrait(character: Character, id: string): boolean {
  return addable(
    TRAIT_CATALOG[id],
    character.traits.some((t) => t.traitId === id),
  );
}

/**
 * The trait added. Amorphous also moves the SHAPE: adding it makes an
 * amorphous body (a horde of them stays a horde) (v8.45.0).
 */
export function addTrait(character: Character, id: string, parameter?: string): Partial<Character> {
  if (!canAddTrait(character, id)) return {};
  const inst: TraitInstance = { traitId: id, ...parameterFor(TRAIT_CATALOG[id], parameter) };
  const update: Partial<Character> = { traits: [...character.traits, inst] };
  // An object stays an object (2026-10-26): its body is not a trait's to change.
  if (id === 'amorphous' && character.bodyType !== 'object')
    update.bodyType = bodyTypeFor('amorphous', isCrowd(character));
  return update;
}

/** A trait no catalog knows, written down by name. */
export function addCustomTrait(character: Character, name: string, note = ''): Partial<Character> {
  return { traits: [...character.traits, { traitId: CUSTOM_ENTRY_ID, ...customFields(name, note) }] };
}

/**
 * The trait at `index` taken off. Removing Amorphous reverts the shape to
 * humanoid and leaves the number alone - a mob that stops being amorphous
 * is a mob of people, not one person (v8.45.0).
 */
export function removeTraitAt(character: Character, index: number): Partial<Character> {
  const update: Partial<Character> = { traits: without(character.traits, index) };
  if (character.traits[index]?.traitId === 'amorphous' && character.bodyType === 'amorphous') {
    update.bodyType = bodyTypeFor('humanoid', isCrowd(character));
  }
  return update;
}

export function setTraitAt(character: Character, index: number, patch: InstancePatch): Partial<Character> {
  return { traits: patched(character.traits, index, patch) };
}
